import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TState } from "@/types/issue-metadata";
import type { TProject } from "@/types/project";
import { STATE_GROUPS } from "@/constants/states";
import type { TStateGroup } from "@/constants/states";
import type { TStateCreateData, TStateUpdateData } from "@/services/work-item-state.service";

export type TGroupedStates = Record<TStateGroup, TState[]>;

function emptyGroups(): TGroupedStates {
  return {
    backlog: [],
    unstarted: [],
    started: [],
    on_hold: [],
    completed: [],
    cancelled: [],
  };
}

export class WorkItemStatesStore {
  isLoading = false;
  isMutating = false;
  mutatingStateId: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getStates(workspaceSlug: string): TState[] {
    return this.rootStore.issueMetadata.statesByWorkspace.get(workspaceSlug) ?? [];
  }

  getGroupedStates(workspaceSlug: string): TGroupedStates {
    const groups = emptyGroups();
    const states = this.getStates(workspaceSlug);
    for (const state of states) {
      const bucket = groups[state.group];
      if (bucket) bucket.push(state);
    }
    for (const group of STATE_GROUPS) {
      groups[group] = [...groups[group]].sort((a, b) => a.sequence - b.sequence);
    }
    return groups;
  }

  get currentWorkspaceSlug(): string | null {
    return this.rootStore.router.workspaceSlug ?? null;
  }

  get currentWorkspaceStates(): TState[] {
    const slug = this.currentWorkspaceSlug;
    return slug ? this.getStates(slug) : [];
  }

  private setStates(workspaceSlug: string, states: TState[]): void {
    this.rootStore.issueMetadata.statesByWorkspace.set(workspaceSlug, states);
  }

  /** States embedded on the current project detail — the read-only source for a
   *  viewer who can't call the workspace-scoped /work-item-states/ endpoint. */
  private embeddedStates(): TState[] {
    const proj = this.rootStore.project.currentProject as TProject | undefined;
    return proj?.work_item_states ?? [];
  }

  async fetchStates(slug: string): Promise<TState[]> {
    this.isLoading = true;
    try {
      const states = await this.rootStore.workItemStateService.list(slug);
      runInAction(() => {
        this.setStates(slug, states);
      });
      return states;
    } catch (err) {
      // The states endpoint is workspace-scoped; a project viewer (no
      // workspace-level read) 403s on it. Fall back to the states embedded on
      // the current project detail so the work-item Board/Table still render.
      console.warn(
        "[work-item-states] list failed; falling back to embedded states",
        { slug },
        err,
      );
      const embedded = this.embeddedStates();
      runInAction(() => this.setStates(slug, embedded));
      return embedded;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createState(slug: string, data: TStateCreateData): Promise<TState> {
    this.isMutating = true;
    try {
      const created = await this.rootStore.workItemStateService.create(slug, data);
      runInAction(() => {
        this.setStates(slug, [...this.getStates(slug), created]);
      });
      return created;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateState(slug: string, stateId: string, data: TStateUpdateData): Promise<TState> {
    const snapshot = this.getStates(slug);
    const target = snapshot.find((s) => s.id === stateId);
    runInAction(() => {
      this.mutatingStateId = stateId;
      if (target) {
        this.setStates(
          slug,
          snapshot.map((s) => (s.id === stateId ? ({ ...s, ...data } as TState) : s)),
        );
      }
    });
    try {
      const updated = await this.rootStore.workItemStateService.update(slug, stateId, data);
      runInAction(() => {
        this.setStates(
          slug,
          this.getStates(slug).map((s) => (s.id === stateId ? updated : s)),
        );
      });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async deleteState(slug: string, stateId: string): Promise<void> {
    const snapshot = this.getStates(slug);
    runInAction(() => {
      this.mutatingStateId = stateId;
      this.setStates(
        slug,
        snapshot.filter((s) => s.id !== stateId),
      );
    });
    try {
      await this.rootStore.workItemStateService.remove(slug, stateId);
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async markDefault(slug: string, stateId: string): Promise<TState> {
    const snapshot = this.getStates(slug);
    runInAction(() => {
      this.mutatingStateId = stateId;
      this.setStates(
        slug,
        snapshot.map((s) => {
          if (s.id === stateId) return { ...s, default: true };
          if (s.default) return { ...s, default: false };
          return s;
        }),
      );
    });
    try {
      const updated = await this.rootStore.workItemStateService.markDefault(slug, stateId);
      runInAction(() => {
        this.setStates(
          slug,
          this.getStates(slug).map((s) => (s.id === stateId ? updated : s)),
        );
      });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async reorder(
    slug: string,
    stateId: string,
    destinationGroup: TStateGroup,
    destinationIndex: number,
  ): Promise<void> {
    const snapshot = [...this.getStates(slug)];
    const target = snapshot.find((s) => s.id === stateId);
    if (!target) return;

    const grouped = this.getGroupedStates(slug);
    const sourceGroup = target.group;
    const isSameGroup = sourceGroup === destinationGroup;

    const withoutSource = grouped[sourceGroup].filter((s) => s.id !== stateId);
    const destBucket = isSameGroup ? withoutSource : [...grouped[destinationGroup]];

    const clampedIndex = Math.max(0, Math.min(destinationIndex, destBucket.length));
    const moved = { ...target, group: destinationGroup };
    destBucket.splice(clampedIndex, 0, moved);

    const resequence = (bucket: TState[]): TState[] =>
      bucket.map((state, idx) => ({ ...state, sequence: idx + 1 }));

    const nextGrouped: TGroupedStates = { ...grouped };
    if (isSameGroup) {
      nextGrouped[destinationGroup] = resequence(destBucket);
    } else {
      nextGrouped[sourceGroup] = resequence(withoutSource);
      nextGrouped[destinationGroup] = resequence(destBucket);
    }

    const flat: TState[] = [];
    for (const group of STATE_GROUPS) {
      flat.push(...nextGrouped[group]);
    }

    runInAction(() => {
      this.setStates(slug, flat);
      this.mutatingStateId = stateId;
    });

    try {
      const updatedFromServer = await this.rootStore.workItemStateService.update(slug, stateId, {
        group: destinationGroup,
        sequence: clampedIndex + 1,
      });
      runInAction(() => {
        this.setStates(
          slug,
          this.getStates(slug).map((s) => (s.id === stateId ? updatedFromServer : s)),
        );
      });
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }
}
